import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

const DEFAULT_EXPIRES_IN_SEC = 3600;
/** Refresh a bit before server expiry to avoid serving a dead URL. */
const EXPIRY_SKEW_MS = 60 * 1000;

/** In-memory cache for the current tenant's logo presigned URL. */
let logoCache = null;

const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens?.idToken) {
      return tokens.idToken.toString();
    }
    return null;
  } catch (error) {
    console.error('[tenantLogoApi] Failed to fetch auth session:', error);
    return null;
  }
};

const authHeaders = token => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

const isCacheValid = () => {
  if (!logoCache?.presigned_url) {
    return false;
  }
  return Date.now() < logoCache.expiresAtMs - EXPIRY_SKEW_MS;
};

const storeCache = payload => {
  const expiresInSec = Number(payload?.expires_in) || DEFAULT_EXPIRES_IN_SEC;
  logoCache = {
    presigned_url: payload.presigned_url || payload.presignedUrl || null,
    logo_content_type:
      payload.logo_content_type || payload.logoContentType || null,
    logo_key: payload.logo_key || payload.logoKey || null,
    expires_in: expiresInSec,
    expiresAtMs: Date.now() + expiresInSec * 1000,
    raw: payload,
  };
  return logoCache;
};

export const clearTenantLogoCache = () => {
  logoCache = null;
};

/**
 * GET /tenant/logo — presigned download URL for the current org logo.
 * Returns null on 404 (no logo set). Caches until expires_in.
 */
export const getTenantLogo = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh && isCacheValid()) {
    return logoCache;
  }

  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${API_BASE_URL}/tenant/logo`, {
    method: 'GET',
    headers: authHeaders(token),
  });

  if (response.status === 404) {
    clearTenantLogoCache();
    return null;
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Failed to fetch tenant logo (${response.status}): ${text || response.statusText}`
    );
  }

  const data = await response.json();
  const payload = data?.data || data?.body || data || {};
  const url =
    payload?.presigned_url ||
    payload?.presignedUrl ||
    data?.presigned_url ||
    data?.presignedUrl ||
    null;
  if (!url) {
    clearTenantLogoCache();
    return null;
  }

  return storeCache({ ...data, ...payload, presigned_url: url });
};

/**
 * Display URL for <img src>. Null when no org logo is configured.
 */
export const getTenantLogoUrl = async (options) => {
  try {
    const logo = await getTenantLogo(options);
    return logo?.presigned_url || null;
  } catch (error) {
    console.warn('[tenantLogoApi] getTenantLogoUrl failed:', error);
    return null;
  }
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result) resolve(reader.result);
      else reject(new Error('Empty data URL'));
    };
    reader.onerror = () => reject(new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });

/** Draw a CORS-enabled image onto canvas (works when S3 sends ACAO). */
const imageUrlToDataUrlViaCanvas = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

const blobLooksLikeImage = (blob, contentType) => {
  const ct = String(contentType || blob?.type || '').toLowerCase();
  if (ct.startsWith('image/')) return true;
  if (!blob || !blob.size) return false;
  if (ct.includes('json') || ct.includes('html') || ct.includes('text/')) {
    return false;
  }
  return blob.size > 64;
};

const extractEmbeddedLogoDataUrl = (payload, fallbackType = 'image/png') => {
  if (!payload || typeof payload !== 'object') return null;
  const raw =
    payload.logo_base64 ||
    payload.logoBase64 ||
    payload.logo_data ||
    payload.data_url ||
    payload.dataUrl ||
    null;
  if (!raw || typeof raw !== 'string') return null;
  if (raw.startsWith('data:')) return raw;
  const mime =
    payload.logo_content_type || payload.logoContentType || fallbackType;
  return `data:${mime};base64,${raw}`;
};

/**
 * Bytes as a data URL for PDF. Never returns an https S3 URL (those cannot be
 * painted by html2canvas). Preview should use getTenantLogoUrl() instead.
 */
export const getTenantLogoDataUrl = async (options) => {
  const token = await getAuthToken().catch(() => null);

  if (token) {
    const proxyPaths = ['/tenant-logo-file'];
    const publicUrl = String(process.env.PUBLIC_URL || '').replace(/\/$/, '');
    if (publicUrl) proxyPaths.push(`${publicUrl}/tenant-logo-file`);

    for (const path of proxyPaths) {
      try {
        const proxied = await fetch(path, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        });
        const ct = proxied.headers.get('content-type') || '';
        if (proxied.ok) {
          const blob = await proxied.blob();
          if (blobLooksLikeImage(blob, ct)) {
            return await blobToDataUrl(blob);
          }
        }
      } catch (error) {
        console.warn('[tenantLogoApi] Same-origin logo proxy failed:', error);
      }
    }
  }

  const logoMeta = await getTenantLogo(options).catch(() => null);
  const embedded = extractEmbeddedLogoDataUrl(logoMeta?.raw || logoMeta);
  if (embedded) return embedded;

  const url = logoMeta?.presigned_url || null;
  if (!url) return null;
  if (url.startsWith('data:')) return url;

  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
    const ct = response.headers.get('content-type') || '';
    if (response.ok) {
      const blob = await response.blob();
      if (blobLooksLikeImage(blob, ct)) {
        return await blobToDataUrl(blob);
      }
    }
  } catch (error) {
    console.warn('[tenantLogoApi] Fetch convert failed, trying canvas:', error);
  }

  return imageUrlToDataUrlViaCanvas(url);
};

export const isTenantLogoDataUrl = (value) =>
  String(value || '').startsWith('data:image/');

export const applyTenantLogoDataUrlToNode = async (node, dataUrl) => {
  if (!node || !isTenantLogoDataUrl(dataUrl)) return false;
  const img = node.querySelector('[data-tenant-logo="1"] img');
  if (!img) return false;
  img.removeAttribute('crossorigin');
  if (img.src !== dataUrl) {
    img.src = dataUrl;
  }
  try {
    await img.decode();
  } catch {
    await new Promise((resolve) => {
      if (img.complete && img.naturalWidth) {
        resolve();
        return;
      }
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
  }
  return img.naturalWidth > 0;
};

/** Draw the org logo onto page 1 after html2canvas (S3 images are often omitted). */
export const stampTenantLogoOnPdf = (pdf, node, logoDataUrl, pageWidth) => {
  if (!pdf || !isTenantLogoDataUrl(logoDataUrl)) return;

  const img = node?.querySelector('[data-tenant-logo="1"] img');
  const nodeRect = node?.getBoundingClientRect?.();
  const rect = img?.getBoundingClientRect?.();
  const docWidthPx = node?.scrollWidth || node?.offsetWidth || 794;
  const mmPerPx = pageWidth / docWidthPx;

  let xMm = 138;
  let yMm = 12;
  let wMm = 58;
  let hMm = 28;

  if (nodeRect && rect && rect.width > 4 && rect.height > 4) {
    xMm = (rect.left - nodeRect.left + (node.scrollLeft || 0)) * mmPerPx;
    yMm = (rect.top - nodeRect.top + (node.scrollTop || 0)) * mmPerPx;
    wMm = Math.max(rect.width * mmPerPx, 12);
    hMm = Math.max(rect.height * mmPerPx, 8);
  }

  const format = /image\/jpe?g/i.test(logoDataUrl) ? 'JPEG' : 'PNG';
  try {
    pdf.setPage(1);
    pdf.addImage(logoDataUrl, format, xMm, yMm, wMm, hMm);
  } catch (error) {
    console.warn('[tenantLogoApi] Failed to stamp logo on PDF:', error);
  }
};

/**
 * POST /tenant/logo/presign?content_type=...
 * Admin / ops write roles only.
 */
export const presignTenantLogoUpload = async (contentType = 'image/png') => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const endpoint = `${API_BASE_URL}/tenant/logo/presign?content_type=${encodeURIComponent(contentType)}`;
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Failed to presign tenant logo upload (${response.status}): ${text || response.statusText}`
    );
  }

  return response.json();
};

/**
 * PUT /tenant/logo — register logo after S3 upload.
 */
export const registerTenantLogo = async ({ logo_key, logo_content_type }) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${API_BASE_URL}/tenant/logo`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ logo_key, logo_content_type }),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Failed to register tenant logo (${response.status}): ${text || response.statusText}`
    );
  }

  clearTenantLogoCache();
  return response.json().catch(() => ({ success: true }));
};

/**
 * DELETE /tenant/logo — clear org logo.
 */
export const deleteTenantLogo = async () => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${API_BASE_URL}/tenant/logo`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Failed to delete tenant logo (${response.status}): ${text || response.statusText}`
    );
  }

  clearTenantLogoCache();
  return true;
};

/**
 * Full upload flow: presign → S3 POST → PUT register.
 * Accepts a File/Blob or a data-URL string (with optional base64 payload).
 */
export const uploadTenantLogo = async (fileOrDataUrl, contentTypeHint) => {
  let blob;
  let contentType = contentTypeHint || 'image/png';

  if (typeof fileOrDataUrl === 'string' && fileOrDataUrl.startsWith('data:')) {
    contentType =
      fileOrDataUrl.match(/data:([^;]+);/)?.[1] || contentType;
    const base64 = fileOrDataUrl.includes(',')
      ? fileOrDataUrl.split(',')[1]
      : fileOrDataUrl;
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType });
  } else if (fileOrDataUrl instanceof Blob) {
    blob = fileOrDataUrl;
    contentType = fileOrDataUrl.type || contentType;
  } else {
    throw new Error('uploadTenantLogo expects a File/Blob or data URL');
  }

  if (blob.size > 5 * 1024 * 1024) {
    throw new Error('Tenant logo must be 5 MB or smaller');
  }

  const presign = await presignTenantLogoUpload(contentType);
  const presignedPost = presign.presigned_post || presign.presignedPost;
  const objectKey = presign.object_key || presign.objectKey || presign.logo_key;

  if (!presignedPost?.url || !presignedPost?.fields || !objectKey) {
    throw new Error('Invalid tenant logo presign response');
  }

  const formData = new FormData();
  Object.entries(presignedPost.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', blob);

  const uploadResponse = await fetch(presignedPost.url, {
    method: 'POST',
    body: formData,
  });

  if (!uploadResponse.ok) {
    const text = await uploadResponse.text().catch(() => '');
    throw new Error(`S3 upload failed (${uploadResponse.status}): ${text}`);
  }

  await registerTenantLogo({
    logo_key: objectKey,
    logo_content_type: contentType,
  });

  return getTenantLogo({ forceRefresh: true });
};
